import React from 'react';
import { ButtonBase, Typography } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import { formatCurrency } from '../../../shared/utils/currency';

export interface DeductionGroupHeaderProps {
  groupName: string;
  count: number;
  totalAmount: number;
  expanded: boolean;
  onToggle: () => void;
  groupedBySite?: boolean;
  'data-testid'?: string;
  panelId?: string;
}

/**
 * DeductionGroupHeader — atom component that renders a collapsible group header
 * showing the groupName, deduction count, totalAmount, and an expand/collapse
 * chevron. Uses `groupedBySite` to switch between BusinessIcon and PersonIcon.
 * Supports `data-testid` for testing and `panelId` for aria-controls.
 */
export const DeductionGroupHeader = React.memo(function DeductionGroupHeader({
  groupName,
  count,
  totalAmount,
  expanded,
  onToggle,
  groupedBySite,
  'data-testid': dataTestId,
  panelId,
}: DeductionGroupHeaderProps) {
  const GroupIcon = groupedBySite ? BusinessIcon : PersonIcon;

  return (
    <ButtonBase
      onClick={onToggle}
      data-testid={dataTestId}
      aria-expanded={expanded}
      aria-controls={panelId}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        width: '100%',
        px: { xs: 2, sm: 3 },
        py: 1,
        justifyContent: 'flex-start',
        bgcolor: 'action.selected',
        '&:hover': {
          bgcolor: 'action.hover',
        },
      }}
    >
      <ChevronRightIcon
        fontSize="small"
        sx={{
          color: 'text.secondary',
          transition: 'transform 150ms ease',
          transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
        }}
      />
      <GroupIcon
        sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }}
      />
      <Typography variant="body2" fontWeight={600}>
        {groupName}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {count}
      </Typography>
      <Typography variant="body2" fontWeight={600} sx={{ ml: 'auto' }}>
        {formatCurrency(totalAmount)}
      </Typography>
    </ButtonBase>
  );
});
